import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { TableSort } from "@src/shared/constants/sort-order.constants";
import {
  userGuideService,
  type UserGuideListItem,
  type UserGuideDetail,
  type GetUserGuidesParams,
  type CreateUserGuidePayload,
  type UpdateUserGuidePayload,
} from "@src/@api-services/userGuide";

interface UserGuideState {
  data: UserGuideListItem[];
  selectedGuide: UserGuideDetail | null;
  total: number;
  params: GetUserGuidesParams;
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
}

export const getUserGuides = createAsyncThunk(
  "userGuides/getUserGuides",
  async (params: GetUserGuidesParams, { rejectWithValue }) => {
    try {
      const response = await userGuideService.getUserGuides(params);
      const rawData = response.data?.data?.data ?? [];
      const totalRecord = response.data?.data?.totalRecord ?? 0;
      return {
        params,
        data: (rawData as UserGuideListItem[]),
        total: Number(totalRecord),
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch user guides"
      );
    }
  }
);

export const getUserGuideDetails = createAsyncThunk(
  "userGuides/getUserGuideDetails",
  async (id: string | number, { rejectWithValue }) => {
    try {
      const response = await userGuideService.getUserGuideById(id);
      return response.data?.data as UserGuideDetail;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch user guide details"
      );
    }
  }
);

export const createUserGuide = createAsyncThunk(
  "userGuides/createUserGuide",
  async (
    payload: CreateUserGuidePayload,
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const response = await userGuideService.createUserGuide(payload);
      const state = getState() as any;
      if (state.userGuides?.params) {
        await dispatch(getUserGuides(state.userGuides.params));
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to create user guide"
      );
    }
  }
);

export const updateUserGuide = createAsyncThunk(
  "userGuides/updateUserGuide",
  async (
    {
      id,
      guideData,
    }: {
      id: string | number;
      guideData: UpdateUserGuidePayload;
    },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const response = await userGuideService.updateUserGuide(id, guideData);
      const state = getState() as any;
      if (state.userGuides?.params) {
        await dispatch(getUserGuides(state.userGuides.params));
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to update user guide"
      );
    }
  }
);

export const deleteUserGuide = createAsyncThunk(
  "userGuides/deleteUserGuide",
  async (id: string | number, { dispatch, getState, rejectWithValue }) => {
    try {
      const response = await userGuideService.deleteUserGuide(id);
      const state = getState() as any;
      if (state.userGuides?.params) {
        await dispatch(getUserGuides(state.userGuides.params));
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to delete user guide"
      );
    }
  }
);

const initialState: UserGuideState = {
  data: [],
  selectedGuide: null,
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: "createdAt",
    orderDirection: TableSort.DESC,
  },
  loading: false,
  actionLoading: false,
  error: null,
};

export const userGuideSlice = createSlice({
  name: "userGuides",
  initialState,
  reducers: {
    clearSelectedUserGuide: (state) => {
      state.selectedGuide = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // List
      .addCase(getUserGuides.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserGuides.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.total = action.payload.total;
        state.params = action.payload.params;
      })
      .addCase(getUserGuides.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to load user guides";
      })
      // Detail
      .addCase(getUserGuideDetails.fulfilled, (state, action) => {
        state.selectedGuide = action.payload;
      })
      // Mutate loading
      .addCase(createUserGuide.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(createUserGuide.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(createUserGuide.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(updateUserGuide.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(updateUserGuide.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(updateUserGuide.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(deleteUserGuide.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(deleteUserGuide.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(deleteUserGuide.rejected, (state) => {
        state.actionLoading = false;
      });
  },
});

export const { clearSelectedUserGuide } = userGuideSlice.actions;

export default userGuideSlice.reducer;
